'use client'
import useSWR from 'swr'

type CacheKey = string | [string, string]

const fetcher = (key: CacheKey) =>
  fetch(Array.isArray(key) ? key[0] : key).then((res) => res.json())

export interface DataResponse<T> {
  data: T | undefined
  isLoading: boolean
  error: any
}

type DataPayload<T> = Record<string, T> | null

/**
 * @param url      request url, or `null` to skip the request
 * @param key      property of the response payload to unwrap
 * @param scope    optional cache scope. It takes part in the SWR cache key but is
 *                 never sent to the server, so responses that depend on the caller's
 *                 session (B2B prices, buyability) can't be served from another
 *                 session's cache. Pass `undefined` for session-independent data.
 */
function useGetCachedRequest<T, Key = string>(
  url: string | null,
  key?: Key,
  scope?: string
): DataResponse<T | null> {
  type Payload = Key extends undefined ? T : DataPayload<T>

  const cacheKey: CacheKey | null = url === null ? null : scope === undefined ? url : [url, scope]

  const { data: payload, error } = useSWR<Payload | { error: string }>(cacheKey, fetcher)

  // @ts-expect-error -- FIX
  if (payload && 'error' in payload) {
    return {
      data: null,
      isLoading: false,
      error: payload.error
    }
  }

  let data: DataResponse<T>['data']

  if (typeof key !== 'undefined') {
    data = payload ? (payload as DataPayload<T>)?.[key as unknown as string] : undefined
  } else {
    data = payload as T
  }

  return {
    data,
    isLoading: !url ? false : !data && !error,
    error
  }
}

export default useGetCachedRequest
